import type { TrackedTranslation } from '@ps/web-core-intl';
import { required } from '@ps/web-shared-validators';
import { range, shuffle } from 'lodash';
import type { ReactNode } from 'react';
import type React from 'react';
import { useMemo } from 'react';
import type { FieldInputProps } from 'react-final-form';
import { useField } from 'react-final-form';

import { DeleteKeyPadIcon } from '../../../assets/icons';
import useIsDesktop from '../../hooks/useIsDesktop/useIsDesktop';
import { TXTCLI_Enter_PIN, TXTWCL_Please_enter_your_PIN } from '../../translations';
import NumericPassword from '../NumericPassword/NumericPassword';

import styles from './PIN.module.scss';
import PINButton from './PINButton';

interface Props {
  id: string;
  name: string;
  label?: TrackedTranslation;
}

const DELETE_ICON_DIMENSIONS = 16;

const deleteIcon = (
  <DeleteKeyPadIcon width={DELETE_ICON_DIMENSIONS} height={DELETE_ICON_DIMENSIONS} />
);

const MAX_PIN_LENGTH = 6;

const generateButtons = () => {
  let buttons: ReactNode[] = range(0, 10);
  const isTest = process.env.STORYBOOK_ENV === 'test' || process.env.NODE_ENV === 'test';
  if (!isTest) {
    buttons = shuffle(buttons);
  }
  buttons.splice(5, 0, deleteIcon);
  return buttons;
};

const PIN: React.FC<Props> = ({ id, name, label }) => {
  const showButtons = useIsDesktop();
  const buttons = useMemo(() => generateButtons(), []);
  const { input } = useField(name);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onClick = (input: FieldInputProps<any, HTMLInputElement>, button: ReactNode) => {
    let pin = input.value;
    pin = button === deleteIcon ? pin.slice(0, -1) : pin + button;
    pin = pin.length > MAX_PIN_LENGTH ? pin.slice(0, MAX_PIN_LENGTH) : pin;
    input.onChange(pin);
  };

  return (
    <>
      <NumericPassword
        id={id}
        label={label || TXTCLI_Enter_PIN}
        maxLength={6}
        validators={[required(TXTWCL_Please_enter_your_PIN)]}
        readOnly={showButtons}
      />
      {showButtons && (
        <div className={styles.buttons}>
          {buttons.map((button, index) => (
            <PINButton key={index} onClick={() => onClick(input, button)}>
              {button}
            </PINButton>
          ))}
        </div>
      )}
    </>
  );
};

export default PIN;
